import { useState } from "react";
import { SectionCard } from "./SectionCard";
import type { ReachTrends } from "../lib/reach";
import {
  PACKET_SIGHTINGS_TITLE,
  SNR_DESCRIPTION,
  TRENDS_TITLE,
  snrBandColorVar,
  snrBandRects,
  snrRange,
  trendBars,
  trendIndexForKey,
  trendIndexAt,
  trendLinePaths,
  trendReadout,
  trendStats,
  trendStatsLine,
  trendEndpointLabel,
  trendsCaption,
} from "../lib/trends";
import type { ReachWindow } from "../lib/reach";

const W = 300;
const H = 82;
const LEFT = 34;
const RIGHT = 4;
const TOP = 5;
const BOTTOM = 13;

type Kind = "bars" | "line" | "snr";

function Sparkline({
  title, description, hours, values, kind, fmt,
}: { title: string; description: string; hours: string[]; values: (number | null)[]; kind: Kind; fmt: (v: number) => string }) {
  const [hover, setHover] = useState<number | null>(null);
  const stats = trendStats(values);
  const selected = hover ?? stats?.latestIndex ?? -1;
  const range = kind === "snr" ? snrRange(values) : { lo: 0, hi: Math.max(1, stats ? stats.max : 1) };
  const lo = kind === "line" ? Math.min(0, stats ? stats.min : 0) : range.lo;
  const span = range.hi - lo || 1;
  const plotW = W - LEFT - RIGHT;
  const plotH = H - TOP - BOTTOM;
  const yOf = (v: number) => TOP + plotH - ((v - lo) / span) * plotH;
  const xOf = (i: number) => LEFT + ((i + 0.5) * plotW) / Math.max(hours.length, 1);
  const readout = selected >= 0 && hours[selected]
    ? trendReadout(hours[selected], values[selected], fmt)
    : "Use the pointer or keyboard to inspect an hour.";
  const latestOrFirst = stats?.latestIndex ?? (hours.length ? 0 : -1);

  const setPointerHour = (clientX: number, rect: DOMRect) => {
    const plotX = ((clientX - rect.left) / (rect.width || W)) * W - LEFT;
    setHover(trendIndexAt(Math.max(0, Math.min(plotW, plotX)), plotW, hours.length));
  };

  return (
    <div className="trend-spark">
      <div className="trend-spark-title">{title}</div>
      <div className="trend-spark-description">{description}</div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        style={{ maxWidth: W, height: H, display: "block", touchAction: "pan-y" }}
        role="slider"
        tabIndex={0}
        aria-label={`${title} by hour. Use left and right arrows to inspect hours; Home and End move to the first and last hour.`}
        aria-valuemin={1}
        aria-valuemax={Math.max(1, hours.length)}
        aria-valuenow={Math.max(1, selected + 1)}
        aria-valuetext={readout}
        onFocus={() => setHover((current) => current ?? latestOrFirst)}
        onKeyDown={(e) => {
          const next = trendIndexForKey(e.key, selected < 0 ? latestOrFirst : selected, hours.length);
          if (next === null) return;
          e.preventDefault();
          setHover(next);
        }}
        onPointerDown={(e) => {
          e.currentTarget.focus();
          setPointerHour(e.clientX, e.currentTarget.getBoundingClientRect());
        }}
        onMouseMove={(e) => {
          setPointerHour(e.clientX, e.currentTarget.getBoundingClientRect());
        }}
        onMouseLeave={(e) => {
          if (document.activeElement !== e.currentTarget) setHover(null);
        }}
      >
        {kind === "snr" && snrBandRects(range.lo, range.hi).map((b) => (
          <rect key={b.band} x={LEFT} y={yOf(b.to)} width={plotW} height={Math.max(0, yOf(b.from) - yOf(b.to))} fill={`var(${snrBandColorVar(b.band)})`} opacity={0.15} />
        ))}
        <line x1={LEFT} x2={W - RIGHT} y1={H - BOTTOM} y2={H - BOTTOM} stroke="var(--border)" strokeWidth={1} />
        <text x={LEFT - 4} y={TOP + 4} textAnchor="end" fill="var(--ink-muted)" fontSize={9}>{fmt(range.hi)}</text>
        <text x={LEFT - 4} y={H - BOTTOM} textAnchor="end" fill="var(--ink-muted)" fontSize={9}>{fmt(lo)}</text>
        {kind === "bars" && trendBars(values, plotW, plotH, 0).map((b) => {
          const zero = values[b.index] === 0;
          const height = zero ? 1 : b.height;
          return <rect key={b.index} x={LEFT + b.x} y={H - BOTTOM - height} width={Math.max(1, b.width)} height={height} fill="var(--reach-hop-0)" opacity={selected === b.index ? 1 : 0.8} />;
        })}
        {kind !== "bars" && trendLinePaths(values, plotW, plotH, lo, range.hi, 0).map((d, i) => (
          <path key={i} d={d} transform={`translate(${LEFT} ${TOP})`} fill="none" stroke="var(--ink)" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
        ))}
        {values.map((v, i) => v === null || v === undefined ? null : <circle key={i} cx={xOf(i)} cy={yOf(v)} r={selected === i ? 3 : 2} fill="var(--ink)" />)}
        {selected >= 0 && (
          <line x1={xOf(selected)} x2={xOf(selected)} y1={TOP} y2={H - BOTTOM} stroke="var(--ink-muted)" strokeWidth={1} opacity={0.6} />
        )}
        <text x={LEFT} y={H - 1} textAnchor="start" fill="var(--ink-muted)" fontSize={9}>{hours[0] ? trendEndpointLabel(hours[0]) : ""}</text>
        <text x={W - RIGHT} y={H - 1} textAnchor="end" fill="var(--ink-muted)" fontSize={9}>{hours.length ? trendEndpointLabel(hours[hours.length - 1]) : ""}</text>
      </svg>
      <div className="trend-spark-stats">{trendStatsLine(stats, hours, fmt)}{stats && <span> · gaps indicate hours without a measurement</span>}</div>
      <div className="trend-spark-hover">{readout}</div>
    </div>
  );
}

const n0 = (v: number) => Math.round(v).toLocaleString("en-US");
const db1 = (v: number) => `${(Math.round(v * 10) / 10).toFixed(1)} dB`;

export function NodeTrendsCard({ trends, windowKey }: { trends: ReachTrends; windowKey: ReachWindow }) {
  return (
    <SectionCard title={TRENDS_TITLE}>
      <div className="reach-caption">{trendsCaption(windowKey)}</div>
      <div className="trend-sparks">
        <Sparkline title={PACKET_SIGHTINGS_TITLE} description="Path appearances and direct receptions, summed across observers." hours={trends.hour_start} values={trends.packets} kind="bars" fmt={n0} />
        <Sparkline title="Observers Hearing It" description="Distinct observers with a sighting in each hour." hours={trends.hour_start} values={trends.observers} kind="line" fmt={n0} />
        <Sparkline title="SNR At Observers" description={SNR_DESCRIPTION} hours={trends.hour_start} values={trends.snr_db} kind="snr" fmt={db1} />
      </div>
    </SectionCard>
  );
}
